import React from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Check, Plug, Loader2 } from "lucide-react";

// Brand colors per provider — premium SaaS feel
const PROVIDER_STYLE = {
    meta: { bg: "bg-blue-50", text: "text-blue-700", letter: "M" },
    google: { bg: "bg-yellow-50", text: "text-yellow-700", letter: "G" },
    youtube: { bg: "bg-red-50", text: "text-red-700", letter: "Y" },
    tiktok: { bg: "bg-pink-50", text: "text-pink-700", letter: "T" },
    snapchat: { bg: "bg-amber-50", text: "text-amber-700", letter: "S" },
    pinterest: { bg: "bg-rose-50", text: "text-rose-700", letter: "P" },
    linkedin: { bg: "bg-sky-50", text: "text-sky-700", letter: "in" },
    reddit: { bg: "bg-orange-50", text: "text-orange-700", letter: "R" },
    x: { bg: "bg-slate-100", text: "text-slate-900", letter: "𝕏" },
    amazon: { bg: "bg-amber-50", text: "text-amber-800", letter: "a" },
    shopify: { bg: "bg-emerald-50", text: "text-emerald-700", letter: "S" },
    stripe: { bg: "bg-violet-50", text: "text-violet-700", letter: "$" },
};

const KIND_LABEL = {
    ads: "AD CHANNEL",
    ecommerce: "E-COMMERCE",
    payments: "PAYMENTS",
};

export default function Integrations() {
    const [items, setItems] = React.useState([]);
    const [busy, setBusy] = React.useState(null);

    const load = React.useCallback(() => {
        api.get("/brand/integrations")
            .then((r) => setItems(r.data))
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message));
    }, []);

    React.useEffect(() => {
        load();
    }, [load]);

    const toggle = async (it) => {
        setBusy(it.provider);
        try {
            await api.post("/brand/integrations", {
                provider: it.provider,
                connected: !it.connected,
                account_label: it.account_label,
            });
            toast.success(
                it.connected ? `${it.label} disconnected` : `${it.label} connected (mocked)`,
            );
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(null);
        }
    };

    if (items.length === 0) {
        return <div className="text-slate-500 text-sm">Loading integrations…</div>;
    }

    const grouped = items.reduce((acc, it) => {
        (acc[it.kind] = acc[it.kind] || []).push(it);
        return acc;
    }, {});

    return (
        <div className="space-y-8" data-testid="integrations-section">
            <div className="card-ecom p-5 surface-warm flex items-center gap-4 flex-wrap">
                <div className="w-12 h-12 rounded-2xl bg-coral/10 flex items-center justify-center flex-shrink-0">
                    <Plug className="w-5 h-5 text-coral" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display text-lg font-semibold text-slate-900">
                        Connect your ad accounts
                    </div>
                    <div className="text-sm text-slate-500">
                        Castline pushes campaigns to the channels you connect. Connections are{" "}
                        <span className="font-semibold">MOCKED</span> in this MVP — real OAuth wires
                        in later.
                    </div>
                </div>
                <span className="chip chip-lime">{items.filter((i) => i.connected).length} connected</span>
            </div>

            {["ads", "ecommerce", "payments"].map((kind) =>
                grouped[kind] ? (
                    <div key={kind} className="space-y-3" data-testid={`integration-group-${kind}`}>
                        <div className="overline text-slate-500">{KIND_LABEL[kind]}</div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {grouped[kind].map((it) => {
                                const style = PROVIDER_STYLE[it.provider] || {
                                    bg: "bg-slate-100",
                                    text: "text-slate-700",
                                    letter: "?",
                                };
                                return (
                                    <div
                                        key={it.provider}
                                        className="card-ecom p-5 flex flex-col"
                                        data-testid={`integration-${it.provider}`}
                                    >
                                        <div className="flex items-start gap-3 mb-3">
                                            <div
                                                className={`w-11 h-11 rounded-xl ${style.bg} ${style.text} flex items-center justify-center font-display font-bold text-lg flex-shrink-0`}
                                            >
                                                {style.letter}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <div className="font-display font-semibold text-slate-900">
                                                        {it.label}
                                                    </div>
                                                    {it.connected && (
                                                        <span className="chip chip-success">
                                                            <Check className="w-3 h-3" /> Connected
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-slate-500 mt-0.5">
                                                    {it.description}
                                                </div>
                                            </div>
                                        </div>
                                        {it.connected && it.account_label && (
                                            <div className="text-xs text-slate-500 mb-3 truncate">
                                                Account:{" "}
                                                <span className="text-slate-800 font-medium">
                                                    {it.account_label}
                                                </span>
                                            </div>
                                        )}
                                        <button
                                            onClick={() => toggle(it)}
                                            disabled={busy === it.provider}
                                            className={`mt-auto ${
                                                it.connected ? "btn-pill-ghost" : "btn-pill-primary"
                                            } w-full inline-flex items-center justify-center disabled:opacity-50`}
                                            data-testid={`toggle-${it.provider}`}
                                        >
                                            {busy === it.provider ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : it.connected ? (
                                                "Disconnect"
                                            ) : (
                                                <>
                                                    <Plug className="w-4 h-4 mr-2" /> Connect
                                                </>
                                            )}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : null,
            )}
        </div>
    );
}
